/** Spine treatment encounter note from Joint Decisions review (E/M documentation). */

export const ENCOUNTER_DECISION = {
  SURGERY: 'surgery',
  NONSURGICAL: 'nonsurgical',
  TESTING: 'testing',
  OPINION: 'opinion',
};

export const ENCOUNTER_DECISION_OPTIONS = [
  { value: ENCOUNTER_DECISION.SURGERY, label: 'Proceed with surgery' },
  { value: ENCOUNTER_DECISION.NONSURGICAL, label: 'Continue nonsurgical treatment' },
  { value: ENCOUNTER_DECISION.TESTING, label: 'Obtain additional testing' },
  { value: ENCOUNTER_DECISION.OPINION, label: 'Seek another opinion' },
];

export const ENCOUNTER_NOTE_TYPE = {
  EM: 'em',
  SURGERY_DECISION: 'surgery_decision',
};

export const ENCOUNTER_NOTE_TYPE_OPTIONS = [
  { value: ENCOUNTER_NOTE_TYPE.SURGERY_DECISION, label: 'Surgery decision' },
  { value: ENCOUNTER_NOTE_TYPE.EM, label: 'E/M coding' },
];

export function emptyEncounterNoteDrafts() {
  return {
    [ENCOUNTER_NOTE_TYPE.EM]: '',
    [ENCOUNTER_NOTE_TYPE.SURGERY_DECISION]: '',
  };
}

export function emptyEncounterNoteEdited() {
  return {
    [ENCOUNTER_NOTE_TYPE.EM]: false,
    [ENCOUNTER_NOTE_TYPE.SURGERY_DECISION]: false,
  };
}

export function defaultEncounterNoteType(decision) {
  return decision === ENCOUNTER_DECISION.SURGERY
    ? ENCOUNTER_NOTE_TYPE.SURGERY_DECISION
    : ENCOUNTER_NOTE_TYPE.EM;
}

export function inferEncounterNoteType(text, fallback = ENCOUNTER_NOTE_TYPE.SURGERY_DECISION) {
  const note = String(text || '');
  if (/JOINT DECISIONS – SPINE TREATMENT ENCOUNTER/.test(note)) {
    return ENCOUNTER_NOTE_TYPE.EM;
  }
  if (/exhausted all reasonable conservative treatment options/.test(note)) {
    return ENCOUNTER_NOTE_TYPE.SURGERY_DECISION;
  }
  return fallback;
}

export function encounterDecisionLabel(value) {
  return ENCOUNTER_DECISION_OPTIONS.find((option) => option.value === value)?.label
    || String(value || '').replace(/_/g, ' ')
    || 'Decision';
}

const DECISION_SENTENCE = {
  [ENCOUNTER_DECISION.SURGERY]: 'proceed with surgery',
  [ENCOUNTER_DECISION.NONSURGICAL]: 'continue nonsurgical treatment',
  [ENCOUNTER_DECISION.TESTING]: 'obtain additional testing',
  [ENCOUNTER_DECISION.OPINION]: 'seek another opinion',
};

const CHART_TAB_NOTE_LABELS = {
  benefits: 'Benefits (pain and function)',
  medical_risks: 'Medical Risks',
  procedure_risks: 'Procedural Risks',
  recovery: 'Recovery',
  long_term: 'Long-Term outcomes',
  value: 'Value',
};

export function chartTabNoteLabel(tabKey) {
  return CHART_TAB_NOTE_LABELS[tabKey] || String(tabKey || '').replace(/_/g, ' ');
}

function uniqueNonEmpty(values) {
  const seen = new Set();
  const out = [];
  (values || []).forEach((value) => {
    const text = String(value || '').trim();
    if (!text || seen.has(text.toLowerCase())) return;
    seen.add(text.toLowerCase());
    out.push(text);
  });
  return out;
}

function joinList(items, { empty = '[not documented]' } = {}) {
  const list = uniqueNonEmpty(items);
  if (list.length === 0) return empty;
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
}

function formatServiceDate(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function parseBmi(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function isObese(bmi, bmiBucketLabel) {
  const n = parseBmi(bmi);
  if (n != null) return n >= 30;
  const label = String(bmiBucketLabel || '').toLowerCase();
  return label.includes('obese') || label.includes('35.0 and over') || label.includes('35+');
}

function isOverweightNotObese(bmi, bmiBucketLabel) {
  if (isObese(bmi, bmiBucketLabel)) return false;
  const n = parseBmi(bmi);
  if (n != null) return n >= 25;
  const label = String(bmiBucketLabel || '').toLowerCase();
  return label.includes('overweight') || label.includes('25.0-34.9');
}

export function encounterTypeFromAppointment(appointmentType) {
  const t = String(appointmentType || '').toUpperCase();
  if (t.includes('TELE')) return 'qualifying telehealth';
  return 'In person';
}

export function patientStatusFromHistory({ isNewPatient } = {}) {
  if (isNewPatient === true) return 'New';
  if (isNewPatient === false) return 'established';
  return 'established';
}

function diabetesFactor(diabetesKey) {
  const key = String(diabetesKey ?? '');
  if (key === '2') return 'diabetes';
  if (key === '1') return 'prediabetes';
  return null;
}

function smokingFactor(smokingKey) {
  return String(smokingKey || '').toUpperCase() === 'YES' ? 'smoking' : null;
}

function antithromboticFactor(antithromboticKey, medicationLabels) {
  const key = String(antithromboticKey ?? '');
  const meds = uniqueNonEmpty(medicationLabels);
  if (key === '2') {
    return meds.length
      ? `unmanaged anticoagulation (${joinList(meds)})`
      : 'unmanaged anticoagulation';
  }
  if (key === '1' || meds.length) {
    return meds.length
      ? `anticoagulation (${joinList(meds)})`
      : 'anticoagulation';
  }
  return null;
}

function ageFactor(age) {
  const n = Number(age);
  if (!Number.isFinite(n)) return null;
  if (n >= 65) return `age ${Math.round(n)}`;
  if (n >= 1) return `age ${Math.round(n)}`;
  return null;
}

export function collectPatientSpecificRisks({
  age,
  bmi,
  bmiBucketLabel,
  smoking,
  diabetes,
  antithrombotic,
  antithromboticMedications,
  revisionSurgery,
} = {}) {
  const factors = [];
  const ageText = ageFactor(age);
  if (ageText) factors.push(ageText);
  if (isObese(bmi, bmiBucketLabel)) factors.push('obesity');
  else if (isOverweightNotObese(bmi, bmiBucketLabel)) factors.push('overweight BMI');
  const smoke = smokingFactor(smoking);
  if (smoke) factors.push(smoke);
  const dm = diabetesFactor(diabetes);
  if (dm) factors.push(dm);
  const at = antithromboticFactor(antithrombotic, antithromboticMedications);
  if (at) factors.push(at);
  if (String(revisionSurgery) === '1') factors.push('prior spine surgery');
  return factors;
}

function procedureSpecificFactors({ reviewedProcedureLabels, hasMultilevel, isRevision } = {}) {
  const factors = [];
  if (isRevision) factors.push('revision surgery');
  if (hasMultilevel) factors.push('multilevel surgery');
  if ((reviewedProcedureLabels || []).length > 1) {
    factors.push('comparison of alternative approaches and implant constructs');
  }
  return factors;
}

function hasMultilevelLabel(labels) {
  return (labels || []).some((label) => /3\+/.test(String(label)));
}

function clinicalProblemParagraph({
  diagnoses,
  regionLabel,
  chiefComplaint,
  functionalImpact,
} = {}) {
  const diagnosis = joinList(diagnoses, {
    empty: regionLabel
      ? `${String(regionLabel).toLowerCase()} degenerative spine disease`
      : '[diagnosis]',
  });
  const progression = 'progression and functional impairment';
  const symptoms = chiefComplaint
    ? String(chiefComplaint)
    : '[symptoms]';
  const functionText = functionalImpact
    || (String(chiefComplaint || '').toLowerCase().includes('work')
      ? 'work'
      : 'walking, work, sleep, or self-care');
  return `The patient has ${diagnosis} with ${progression}. Current symptoms include ${symptoms}. The condition affects ${functionText}.`;
}

function previousTreatmentParagraph({
  previousSpineSurgeries,
  isRevision,
} = {}) {
  const treatments = ['physical therapy, medications, injections, and activity modification'];
  if (isRevision || (previousSpineSurgeries && String(previousSpineSurgeries) !== '0')) {
    const n = previousSpineSurgeries && String(previousSpineSurgeries) !== '0'
      ? previousSpineSurgeries
      : 'prior';
    treatments.push(`${n} spine surgery`);
  }
  return `The patient has completed ${treatments.join(', ')}, with [response]. Continued nonoperative treatment remains an option but is expected to [anticipated benefit/limitation].`;
}

function informationReviewedLines({
  reviewedProcedureLabels,
  viewedChartLabels,
} = {}) {
  const procedures = joinList(reviewedProcedureLabels, { empty: '[procedure comparison]' });
  const tabs = joinList(viewedChartLabels, { empty: 'Benefits' });
  return [
    '[Imaging study and date]',
    '[Laboratory or diagnostic test]',
    '[Prior physician/external note]',
    '[Independent historian, if required]',
    `Independent interpretation personally performed—not separately reported: Joint Decisions comparison of ${procedures}, including ${tabs}.`,
    '[Discussion with external physician or qualified professional]',
  ];
}

function optionsDiscussedLines({
  reviewedProcedureLabels,
  selectedProcedureLabels,
} = {}) {
  const proposed = uniqueNonEmpty(
    selectedProcedureLabels?.length ? selectedProcedureLabels : reviewedProcedureLabels,
  );
  const alternatives = uniqueNonEmpty(reviewedProcedureLabels).filter(
    (label) => !proposed.includes(label),
  );
  const lines = [
    'Continue or modify conservative treatment: continued physical therapy, medications, injections, and activity modification as clinically appropriate',
    'Additional diagnostic or interventional treatment: [details]',
  ];
  if (proposed.length) {
    lines.push(`Proposed surgery: ${joinList(proposed)}`);
  } else {
    lines.push('Proposed surgery: [procedure and levels]');
  }
  if (alternatives.length) {
    lines.push(`Alternative procedures reviewed: ${joinList(alternatives)}`);
  }
  lines.push('No additional treatment at this time');
  return lines;
}

function decisionSentence(decision) {
  return DECISION_SENTENCE[decision] || DECISION_SENTENCE[ENCOUNTER_DECISION.SURGERY];
}

function planParagraph({ decision, selectedProcedureLabels, reviewedProcedureLabels } = {}) {
  const proposed = joinList(
    selectedProcedureLabels?.length ? selectedProcedureLabels : reviewedProcedureLabels,
    { empty: '[procedure]' },
  );
  if (decision === ENCOUNTER_DECISION.NONSURGICAL) {
    return `Continue nonsurgical care with follow-up. Reassess if symptoms progress. ${proposed !== '[procedure]' ? `Surgery (${proposed}) remains an option if nonoperative treatment fails.` : ''}`.trim();
  }
  if (decision === ENCOUNTER_DECISION.TESTING) {
    return 'Obtain additional testing as discussed and follow up to complete shared decision-making.';
  }
  if (decision === ENCOUNTER_DECISION.OPINION) {
    return 'Arrange another opinion as discussed and follow up thereafter.';
  }
  return `Proceed with ${proposed}. Coordinate perioperative planning, optimization requirements, and follow-up.`;
}

function mdmSupport({
  decision,
  reviewedProcedureLabels,
  viewedChartLabels,
  patientRisks,
  procedureRisks,
} = {}) {
  const procedureCount = uniqueNonEmpty(reviewedProcedureLabels).length;
  const tabCount = uniqueNonEmpty(viewedChartLabels).length;
  const riskCount = (patientRisks || []).length + (procedureRisks || []).length;
  const proceeding = decision === ENCOUNTER_DECISION.SURGERY;

  const highProblem = proceeding || procedureCount >= 2;
  const problemsLevel = highProblem ? 'high' : 'moderate';
  const problemsExplanation = highProblem
    ? 'surgical decision-making for a progressive spinal condition with functional impairment and comparison of management options'
    : 'decision-making for a spinal condition with functional impairment; surgery considered but not selected today';

  const dataCategories = [
    'review of Joint Decisions application responses with the patient',
    procedureCount > 0
      ? `independent interpretation of comparative ${tabCount > 1 ? 'outcome, risk, and recovery' : 'outcome'} data for ${procedureCount} procedure${procedureCount === 1 ? '' : 's'}`
      : 'independent interpretation of comparative outcome data',
  ];
  const dataLevel = procedureCount >= 2 && tabCount >= 2 ? 'extensive (high)' : 'moderate';

  const managementLevel = proceeding || riskCount >= 2 ? 'high' : 'moderate';
  const managementDetail = proceeding
    ? `decision for surgery today, including individualized risks (${joinList(
        [...(patientRisks || []), ...(procedureRisks || [])],
        { empty: 'procedure-specific surgical risk' },
      )})`
    : 'management of a condition with potential for surgical intervention; surgery deferred today';

  const finalLevel =
    problemsLevel === 'high' || managementLevel === 'high' || dataLevel.includes('high')
      ? 'high'
      : 'moderate';

  return {
    problemsLevel,
    problemsExplanation,
    dataLevel,
    dataCategories,
    managementLevel,
    managementDetail,
    finalLevel,
  };
}

function suggestedCode({ patientStatus, mdmLevel } = {}) {
  const isNew = String(patientStatus || '').toLowerCase() === 'new';
  const high = mdmLevel === 'high';
  if (isNew) return high ? '99205' : '99204';
  return high ? '99215' : '99214';
}

/**
 * Build the Joint Decisions spine treatment encounter note.
 * Known review data is filled in; remaining coding fields stay as [brackets].
 */
export function buildJointDecisionsEncounterNote(input = {}) {
  const {
    dateOfService,
    appointmentType,
    isNewPatient,
    diagnoses,
    regionLabel,
    chiefComplaint,
    functionalImpact,
    previousSpineSurgeries,
    revisionSurgery,
    reviewedProcedureLabels,
    selectedProcedureLabels,
    viewedChartLabels,
    age,
    bmi,
    bmiBucketLabel,
    smoking,
    diabetes,
    antithrombotic,
    antithromboticMedications,
    decision = ENCOUNTER_DECISION.SURGERY,
  } = input;

  const isRevision = String(revisionSurgery) === '1';
  const patientStatus = patientStatusFromHistory({ isNewPatient });
  const encounterType = encounterTypeFromAppointment(appointmentType);
  const patientRisks = collectPatientSpecificRisks({
    age,
    bmi,
    bmiBucketLabel,
    smoking,
    diabetes,
    antithrombotic,
    antithromboticMedications,
    revisionSurgery,
  });
  const procedureRisks = procedureSpecificFactors({
    reviewedProcedureLabels,
    hasMultilevel: hasMultilevelLabel([
      ...(reviewedProcedureLabels || []),
      ...(selectedProcedureLabels || []),
    ]),
    isRevision,
  });
  const mdm = mdmSupport({
    decision,
    reviewedProcedureLabels,
    viewedChartLabels,
    patientRisks,
    procedureRisks,
  });
  const code = suggestedCode({ patientStatus, mdmLevel: mdm.finalLevel });
  const modifier57 =
    decision === ENCOUNTER_DECISION.SURGERY ? 'Applicable' : 'not applicable';

  const infoLines = informationReviewedLines({
    reviewedProcedureLabels,
    viewedChartLabels,
  });
  const optionLines = optionsDiscussedLines({
    reviewedProcedureLabels,
    selectedProcedureLabels,
  });

  return [
    'JOINT DECISIONS – SPINE TREATMENT ENCOUNTER',
    '',
    `Date of service: ${formatServiceDate(dateOfService)}`,
    `Encounter type: ${encounterType}`,
    `Patient status: ${patientStatus}`,
    'Reason for separate encounter: Shared surgical decision-making using Joint Decisions comparative risk and benefit data, which could not appropriately be completed as part of another encounter',
    '',
    'Clinical problem:',
    clinicalProblemParagraph({
      diagnoses,
      regionLabel,
      chiefComplaint,
      functionalImpact,
    }),
    '',
    'Previous treatment:',
    previousTreatmentParagraph({
      previousSpineSurgeries,
      isRevision,
    }),
    '',
    'Information reviewed today:',
    '',
    ...infoLines.map((line) => line),
    '',
    'Application review:',
    'The patient completed the Joint Decisions application before the encounter. I personally reviewed its responses, verified material information with the patient, and addressed the patient’s stated goals, concerns, and treatment preferences. Application- or staff-only time was not counted as my E/M time.',
    '',
    'Options discussed:',
    '',
    ...optionLines,
    '',
    'Individualized surgical risks:',
    `Patient-specific factors include ${joinList(patientRisks, { empty: '[age, osteoporosis, smoking, obesity, diabetes, anticoagulation, cardiac/pulmonary disease, prior surgery, neurologic deficit, frailty, or other applicable factors]' })}. Procedure-specific considerations include ${joinList(procedureRisks, { empty: '[multilevel surgery, revision surgery, approach, blood loss, implant considerations, or other factors]' })}.`,
    '',
    'We discussed expected benefits, reasonable alternatives, material risks, the possibility of continued symptoms, and the consequences of postponing or declining surgery. The patient demonstrated understanding using [teach-back/questions].',
    '',
    'Decision:',
    `After considering the alternatives and the patient’s preferences, we jointly decided to ${decisionSentence(decision)}. This decision was not predetermined before today’s encounter.`,
    '',
    'Plan:',
    planParagraph({
      decision,
      selectedProcedureLabels,
      reviewedProcedureLabels,
    }),
    '',
    'MDM support:',
    '',
    `Problems addressed: ${mdm.problemsLevel}, ${mdm.problemsExplanation}`,
    `Data reviewed/analyzed: ${mdm.dataLevel}; ${mdm.dataCategories.join('; ')}`,
    `Management risk: ${mdm.managementLevel}, ${mdm.managementDetail}`,
    `Final MDM level: ${mdm.finalLevel}`,
    '',
    'If coding by time:',
    'I personally spent [TOTAL] minutes on this date performing qualifying work, including record review, patient evaluation, counseling, ordering, care coordination, and documentation. Staff and application completion time were excluded.',
    '',
    `Suggested code subject to final coding review: ${code}`,
    `Modifier 57: ${modifier57}, based on procedure global period and timing of the initial decision for surgery.`,
  ].join('\n');
}

const FUSION_PROCEDURE_KEYS = new Set([
  'acdf',
  'in_situ_fusion',
  'cervical_psf_instrumentation',
  'thoracic_psf_instrumentation',
  'lumbar_psf_instrumentation',
  'standalone_alif',
  'alif_with_psf',
  'olif',
  'xlif',
  'plif',
  'tlif',
]);

const ANTERIOR_LATERAL_PROCEDURE_KEYS = new Set([
  'standalone_alif',
  'alif_with_psf',
  'olif',
  'xlif',
]);

const DISCECTOMY_PROCEDURE_KEYS = new Set([
  'hemilaminotomy_micro_discectomy',
  'hemilaminotomy_micro_discectomy_barricade',
  'lumbar_hemilaminotomy_foraminotomy',
  'cervical_hemilaminotomy_foraminotomy',
]);

export function isFusionProcedureKey(procedureKey) {
  const key = String(procedureKey || '');
  if (!key) return false;
  if (FUSION_PROCEDURE_KEYS.has(key)) return true;
  return /(fusion|alif|olif|xlif|plif|tlif|acdf|psf)/i.test(key);
}

export function isAnteriorLateralProcedureKey(procedureKey) {
  const key = String(procedureKey || '');
  if (!key) return false;
  if (ANTERIOR_LATERAL_PROCEDURE_KEYS.has(key)) return true;
  return /(alif|olif|xlif)/i.test(key);
}

export function isDiscectomyProcedureKey(procedureKey) {
  const key = String(procedureKey || '');
  if (!key) return false;
  if (DISCECTOMY_PROCEDURE_KEYS.has(key)) return true;
  return /(discectomy|foraminotomy|hemilaminotomy)/i.test(key);
}

export function isCervicalProcedureContext({ procedureKeys, regionLabel } = {}) {
  if (/cervical/i.test(String(regionLabel || ''))) return true;
  return (procedureKeys || []).some((key) => /(cervical|^acdf$|acdf_)/i.test(String(key || '')));
}

export function regionLevelPlaceholder(regionLabel) {
  const region = String(regionLabel || '').toLowerCase();
  if (region.includes('cervical')) return '[C#–C#]';
  if (region.includes('thoracic')) return '[T#–T#]';
  if (region.includes('lumbar')) return '[L#–L#]';
  return '[levels]';
}

export const COFLEX_DEFAULT_LEVELS = 'L2–L4';

export function isCoflexProcedureKey(key) {
  return /coflex/i.test(String(key || ''));
}

export function defaultProcedureLevelsText({ procedureKeys, procedureKey } = {}) {
  const keys = [...(procedureKeys || [])];
  if (procedureKey) keys.push(procedureKey);
  if (keys.some(isCoflexProcedureKey)) return COFLEX_DEFAULT_LEVELS;
  return '';
}

export function patientPronouns(sex) {
  const value = String(sex ?? '').trim().toUpperCase();
  if (value === 'F' || value === 'FEMALE' || value === '1') {
    return { subject: 'she', Subject: 'She', possessive: 'her', object: 'her' };
  }
  if (value === 'M' || value === 'MALE' || value === '0') {
    return { subject: 'he', Subject: 'He', possessive: 'his', object: 'him' };
  }
  return {
    subject: 'the patient',
    Subject: 'The patient',
    possessive: "the patient's",
    object: 'the patient',
  };
}

export function surgeryDecisionComplications({ procedureKeys } = {}) {
  const keys = procedureKeys || [];
  const fusion = keys.some(isFusionProcedureKey);
  const anterior = keys.some(isAnteriorLateralProcedureKey);
  const discectomy = keys.some(isDiscectomyProcedureKey);
  const items = [
    'death',
    'MI',
    'stroke',
    'PE',
    'DVT',
    'neurological injury',
    'injury to adjacent structures',
    'CSF leak',
    'bleeding',
    'infection',
    'persistent pain',
  ];
  if (fusion) {
    items.push('non-union');
    items.push('hardware failure');
  }
  if (discectomy) items.push('reherniation');
  if (anterior) items.push('abdominal hernia');
  items.push('need for revision surgery');
  return items;
}

function regionAdjective(regionLabel) {
  const region = String(regionLabel || '').trim().toLowerCase();
  return region || '';
}

function resolvedProcedureKeys({ selectedProcedureKeys, reviewedProcedureKeys } = {}) {
  const selected = uniqueNonEmpty(selectedProcedureKeys);
  if (selected.length) return selected;
  return uniqueNonEmpty(reviewedProcedureKeys);
}

function surgeryPhrase({
  selectedProcedureLabels,
  reviewedProcedureLabels,
  procedureLevelsText,
  regionLabel,
  selectedProcedureKeys,
  reviewedProcedureKeys,
} = {}) {
  const proposed = joinList(
    selectedProcedureLabels?.length ? selectedProcedureLabels : reviewedProcedureLabels,
    { empty: '[procedure]' },
  );
  const levels = String(procedureLevelsText || '').trim()
    || defaultProcedureLevelsText({
      procedureKeys: resolvedProcedureKeys({ selectedProcedureKeys, reviewedProcedureKeys }),
    })
    || regionLevelPlaceholder(regionLabel);
  return `${levels} ${proposed}`;
}

function postoperativeSetting(valueSetting) {
  const setting = String(valueSetting || '').toLowerCase();
  if (setting === 'asc') return 'at the surgery center';
  if (setting === 'office') return 'in the office';
  if (setting === 'hopd' || setting === 'inpatient') return 'in the hospital';
  return 'in the hospital or surgery center';
}

function restrictionDeviceClause({
  isCervical,
  includeCollar,
  includeOrthosis,
  regionLabel,
} = {}) {
  if (isCervical && includeCollar) return 'use of a cervical collar';
  if (!includeOrthosis) return null;
  const region = String(regionLabel || '').toLowerCase();
  if (region.includes('lumbar')) return 'use of a lumbar orthosis';
  if (region.includes('thoracic')) return 'use of a thoracolumbar orthosis';
  return 'use of an orthosis';
}

function surgeryDecisionAction({
  decision,
  regionLabel,
  selectedProcedureLabels,
  reviewedProcedureLabels,
  procedureLevelsText,
  selectedProcedureKeys,
  reviewedProcedureKeys,
} = {}) {
  const region = regionAdjective(regionLabel);
  const elective = region ? `elective ${region} spine surgery` : 'elective spine surgery';
  const phrase = surgeryPhrase({
    selectedProcedureLabels,
    reviewedProcedureLabels,
    procedureLevelsText,
    regionLabel,
    selectedProcedureKeys,
    reviewedProcedureKeys,
  });
  if (decision === ENCOUNTER_DECISION.NONSURGICAL) {
    return `continue nonsurgical treatment rather than proceed with ${elective} at this time`;
  }
  if (decision === ENCOUNTER_DECISION.TESTING) {
    return `obtain additional testing before a final decision on ${elective}`;
  }
  if (decision === ENCOUNTER_DECISION.OPINION) {
    return `seek another opinion before proceeding with ${elective}`;
  }
  return `move forward with ${elective} (${phrase})`;
}

/**
 * Practice surgery-decision / informed-consent note filled from Joint Decisions.
 */
export function buildSurgeryDecisionNote(input = {}) {
  const {
    diagnoses,
    regionLabel,
    previousSpineSurgeries,
    revisionSurgery,
    reviewedProcedureLabels,
    selectedProcedureLabels,
    reviewedProcedureKeys,
    selectedProcedureKeys,
    viewedChartLabels,
    age,
    bmi,
    bmiBucketLabel,
    smoking,
    diabetes,
    antithrombotic,
    antithromboticMedications,
    decision = ENCOUNTER_DECISION.SURGERY,
    sex,
    procedureLevelsText,
    valueSetting,
    includeOrthosis = false,
    includeBoneStimulator = false,
    includeCollar = false,
    includeFacetTakedown = false,
  } = input;

  const proceeding = decision === ENCOUNTER_DECISION.SURGERY;
  const pronouns = patientPronouns(sex);
  const procedureKeys = resolvedProcedureKeys({
    selectedProcedureKeys,
    reviewedProcedureKeys,
  });
  const fusion = procedureKeys.some(isFusionProcedureKey);
  const cervical = isCervicalProcedureContext({ procedureKeys, regionLabel });
  const patientRisks = collectPatientSpecificRisks({
    age,
    bmi,
    bmiBucketLabel,
    smoking,
    diabetes,
    antithrombotic,
    antithromboticMedications,
    revisionSurgery,
  });
  const proposed = uniqueNonEmpty(
    selectedProcedureLabels?.length ? selectedProcedureLabels : reviewedProcedureLabels,
  );
  const alternatives = uniqueNonEmpty(reviewedProcedureLabels).filter(
    (label) => !proposed.includes(label),
  );
  const isRevision = String(revisionSurgery) === '1';
  const priorCount = previousSpineSurgeries && String(previousSpineSurgeries) !== '0'
    ? previousSpineSurgeries
    : (isRevision ? 'prior' : null);
  const conservative = priorCount
    ? `The patient has exhausted all reasonable conservative treatment options, including prior spine surgery (${priorCount}).`
    : 'The patient has exhausted all reasonable conservative treatment options.';
  const diagnosisText = joinList(diagnoses, { empty: '' });
  const diagnosisClause = diagnosisText ? ` of ${diagnosisText}` : '';
  const riskClause = patientRisks.length
    ? `, including ${joinList(patientRisks)}`
    : '';
  const action = surgeryDecisionAction({
    decision,
    regionLabel,
    selectedProcedureLabels,
    reviewedProcedureLabels,
    procedureLevelsText,
    selectedProcedureKeys,
    reviewedProcedureKeys,
  });
  const discussion = `After a complete discussion of the natural history${diagnosisClause}, clinical issues, different treatment options available for ${pronouns.possessive} condition, and identified patient and procedure risk factors${riskClause}, ${pronouns.subject} has decided to ${action}.`;
  const alternativeSentence = alternatives.length
    ? ` Alternatives reviewed in Joint Decisions included ${joinList(alternatives)}.`
    : '';
  const tabs = joinList(viewedChartLabels, { empty: '' });
  const reviewSentence = tabs
    ? ` We reviewed comparative Joint Decisions data for ${tabs}.`
    : ' We reviewed comparative Joint Decisions data with the patient.';
  const complications = joinList(surgeryDecisionComplications({ procedureKeys }));
  const setting = postoperativeSetting(valueSetting);
  const device = restrictionDeviceClause({
    isCervical: cervical,
    includeCollar,
    includeOrthosis,
    regionLabel,
  });
  const deviceClause = device ? `, ${device}` : '';
  const viewedRecovery = (viewedChartLabels || []).some((label) => /recovery/i.test(String(label)));
  const recoverySentence = viewedRecovery
    ? ' Expected recovery discussed included operative time and length of stay.'
    : '';
  const paragraphs = [
    `${conservative} ${discussion}${alternativeSentence}`,
    `The patient is aware of the risk/benefit ratio for surgery given ${pronouns.possessive} condition, including uncertainty with any outcome.${reviewSentence} Complications of the anesthesia and surgery can include but are not limited to ${complications}.`,
    `We also discussed the postoperative course ${setting} as well as once discharged in regards to pain management, wound care, driving, lifting${deviceClause}, and other restrictions.${recoverySentence} If necessary, we will obtain complete imaging studies including XR/MRI/CT prior to surgery. A review of all prior imaging studies, as well as any external care, has been completed.`,
  ];
  if (includeOrthosis) {
    paragraphs.push('An orthosis will be ordered or provided today.');
  }
  if (proceeding && includeBoneStimulator && fusion) {
    paragraphs.push('A bone growth stimulator may be ordered for the patient to help facilitate healing in a spinal fusion. This device will help increase the success rate of the overall fusion. The patient understands the benefits and instructions on how to utilize the device properly.');
  }
  const closing = [
    "Once all the patient's (and family member's) questions were answered and I felt satisfied with their understanding of the procedure, we closed our meeting.",
  ];
  if (proceeding) {
    closing.push('Labwork including but not limited to CBC, BMP, PT/PTT, and INR and an EKG will be ordered and reviewed by me prior to surgical intervention.');
  }
  closing.push('Any and all spine injections performed by interventional doctors have been reviewed by me and the results have been interpreted to formulate this surgical plan.');
  paragraphs.push(closing.join(' '));
  if (includeFacetTakedown) {
    paragraphs.push('In order to adequately decompress the neuroforamen, it is medically necessary to take down more than 50% of the facet joints, resulting in destabilization of the spine.');
  }
  return paragraphs.join('\n\n');
}

export function buildEncounterNote(noteType, input = {}) {
  if (noteType === ENCOUNTER_NOTE_TYPE.SURGERY_DECISION) {
    return buildSurgeryDecisionNote(input);
  }
  return buildJointDecisionsEncounterNote(input);
}

export function encounterNoteUiFromChart(chartUi = {}, encounterNote = '') {
  const type = chartUi.note_type
    || inferEncounterNoteType(encounterNote, ENCOUNTER_NOTE_TYPE.SURGERY_DECISION);
  const notes = {
    ...emptyEncounterNoteDrafts(),
    ...(chartUi.notes || {}),
  };
  if (encounterNote && !notes[type]) {
    notes[type] = encounterNote;
  }
  return {
    noteType: type,
    notes,
    edited: {
      [ENCOUNTER_NOTE_TYPE.EM]: Boolean(notes[ENCOUNTER_NOTE_TYPE.EM]),
      [ENCOUNTER_NOTE_TYPE.SURGERY_DECISION]: Boolean(notes[ENCOUNTER_NOTE_TYPE.SURGERY_DECISION]),
    },
    procedureLevelsText: chartUi.procedure_levels_text || '',
    includeOrthosis: Boolean(chartUi.include_orthosis),
    includeBoneStimulator: Boolean(chartUi.include_bone_stimulator),
    includeCollar: Boolean(chartUi.include_collar),
    includeFacetTakedown: Boolean(chartUi.include_facet_takedown),
  };
}

export function chartUiWithEncounterNote(chartUi = {}, extras = {}) {
  return {
    ...chartUi,
    note_type: extras.noteType || ENCOUNTER_NOTE_TYPE.SURGERY_DECISION,
    notes: extras.notes || emptyEncounterNoteDrafts(),
    procedure_levels_text: extras.procedureLevelsText || '',
    include_orthosis: Boolean(extras.includeOrthosis),
    include_bone_stimulator: Boolean(extras.includeBoneStimulator),
    include_collar: Boolean(extras.includeCollar),
    include_facet_takedown: Boolean(extras.includeFacetTakedown),
  };
}
